import {
  Category,
  CategoryRatioInfo,
  EventFinancialDetails,
  EventItem,
  FinancialSummary,
  FundingModel,
  RuleConfig,
  StibetType,
  TaxTier,
} from './types.ts';
import { DEFAULT_CATEGORY_MAX_RATIOS } from './linter.ts';

export function calculateEventFinancials(event: EventItem): EventFinancialDetails {
  const capacity = Math.max(0, Number(event.capacity) || 0);
  const organisers = Math.max(0, Number(event.organisers) || 0);
  const totalEligibleParticipants = capacity + organisers;

  const fixedCost = Math.max(0, Number(event.fixedCost) || 0);
  const variableCost = Math.max(0, Number(event.costPerPerson) || 0) * capacity;
  const totalCost = fixedCost + variableCost;

  // Infer default funding model if omitted
  let fundingModel: FundingModel = event.fundingModel || 'self-funded';
  if (!event.fundingModel) {
    if (Number(event.price) === 0) {
      fundingModel = 'fully-free';
    } else {
      fundingModel = 'self-funded';
    }
  }

  // Infer STIBET type if omitted
  const stibetType: StibetType = event.stibetType || (event.category === 'Trip' ? 'one-day-excursion' : 'general');
  const excursionDays = Math.max(1, Number(event.excursionDays) || 1);

  // Non-fundable expenses (alcohol, pfand, external non-DE costs, running costs)
  const nonFundableCost = Math.min(totalCost, Math.max(0, Number(event.nonFundableCost) || 0));
  const eligibleCostForStibet = Math.max(0, totalCost - nonFundableCost);

  // STIBET Funding Calculation
  let maxStibetAllowanceRaw = 0;
  let maxStibetWithBuffer = 0;
  let appliedStibetFunding = 0;

  const isStibetEligible = fundingModel === 'stibet-free' || fundingModel === 'stibet-partial';

  if (isStibetEligible) {
    if (stibetType === 'general') {
      // General limit: up to €20 per participant
      maxStibetAllowanceRaw = totalEligibleParticipants * 20;
    } else if (stibetType === 'one-day-excursion') {
      // One-day excursion: up to €20 per participant, max 75% of eligible costs
      const perHeadCap = totalEligibleParticipants * 20;
      const percentCap = eligibleCostForStibet * 0.75;
      maxStibetAllowanceRaw = Math.min(perHeadCap, percentCap);
    } else if (stibetType === 'multi-day-excursion') {
      // Multi-day excursion: up to €30 per participant per day, max 75% of eligible costs
      const perHeadCap = totalEligibleParticipants * 30 * excursionDays;
      const percentCap = eligibleCostForStibet * 0.75;
      maxStibetAllowanceRaw = Math.min(perHeadCap, percentCap);
    }

    // Apply mandatory 10% safety buffer for planning
    maxStibetWithBuffer = maxStibetAllowanceRaw * 0.90;
    appliedStibetFunding = Math.min(maxStibetWithBuffer, eligibleCostForStibet);
  }

  // External funding calculation
  let externalFunding = Math.max(0, Number(event.externalFunding) || 0);
  if (fundingModel === 'external' && externalFunding === 0) {
    externalFunding = totalCost; // Default external event to cover 100% of cost
  }

  // Net cost to recover from participant ticket fees
  const netCostToRecover = Math.max(0, totalCost - appliedStibetFunding - externalFunding);

  // Tax Tier determination
  let taxTier: TaxTier = event.taxTier || 'none';
  if (!event.taxTier) {
    if (event.category === 'Drinking') {
      taxTier = 'alcohol';
    } else if (event.category === 'Culture' || event.category === 'Art') {
      taxTier = 'cultural';
    } else {
      taxTier = 'none';
    }
  }

  let taxRatePercent = 0.0;
  if (taxTier === 'cultural') {
    taxRatePercent = 0.07;
  } else if (taxTier === 'alcohol') {
    taxRatePercent = 0.19;
  }

  const appFeePercent = 0.10;
  // Formula: Effective Factor = (1 / (1 + taxRate)) - appFeePercent
  const effectiveTaxFactor = (1 / (1 + taxRatePercent)) - appFeePercent;

  const requiredGrossIncome = effectiveTaxFactor > 0 ? netCostToRecover / effectiveTaxFactor : 0;
  const recommendedMinPricePerParticipant = capacity > 0 ? requiredGrossIncome / capacity : 0;

  // Actual ticket price for participants
  const actualPricePerParticipant = (fundingModel === 'fully-free' || fundingModel === 'stibet-free')
    ? 0
    : Math.max(0, Number(event.price) || 0);

  const projectedParticipantRevenue = actualPricePerParticipant * capacity;
  const netRevenueAfterAppFeeAndTax = projectedParticipantRevenue * effectiveTaxFactor;

  // Section Net Balance (Net Profit or Net Loss / Out-of-Pocket Cost for the section)
  const sectionNetBalance = netRevenueAfterAppFeeAndTax + appliedStibetFunding + externalFunding - totalCost;

  return {
    eventId: event.id,
    title: event.title,
    category: event.category || 'Other',
    fundingModel,
    stibetType,
    taxTier,
    capacity,
    organisers,
    totalEligibleParticipants,
    fixedCost,
    variableCost,
    totalCost,
    nonFundableCost,
    eligibleCostForStibet,
    maxStibetAllowanceRaw,
    maxStibetWithBuffer,
    appliedStibetFunding,
    externalFunding,
    netCostToRecover,
    appFeePercent,
    taxRatePercent,
    effectiveTaxFactor,
    requiredGrossIncome,
    recommendedMinPricePerParticipant,
    actualPricePerParticipant,
    projectedParticipantRevenue,
    netRevenueAfterAppFeeAndTax,
    sectionNetBalance,
  };
}

export function calculateFinancials(events: EventItem[], config?: RuleConfig): FinancialSummary {
  let totalFixedCosts = 0;
  let totalVariableCostsAtCap = 0;
  let totalCostAtCap = 0;
  let totalNonFundableCosts = 0;

  let totalStibetFundingUsed = 0;
  let totalStibetBufferSavings = 0;

  let totalExternalFunding = 0;
  let totalParticipantRevenue = 0;
  let totalNetRevenueAfterTax = 0;
  let totalSectionNetBalance = 0;

  let totalCapacity = 0;
  let totalOrganisersAssigned = 0;

  const categories: Category[] = ['Culture', 'Drinking', 'Sports', 'Social', 'Academic', 'Trip', 'Art', 'Other'];

  const categoryBreakdown: Record<Category, number> = {
    Culture: 0, Drinking: 0, Sports: 0, Social: 0, Academic: 0, Trip: 0, Art: 0, Other: 0,
  };

  const categorySpots: Record<Category, number> = {
    Culture: 0, Drinking: 0, Sports: 0, Social: 0, Academic: 0, Trip: 0, Art: 0, Other: 0,
  };

  const categoryOrgs: Record<Category, number> = {
    Culture: 0, Drinking: 0, Sports: 0, Social: 0, Academic: 0, Trip: 0, Art: 0, Other: 0,
  };

  const eventsByFundingModel: Record<FundingModel, number> = {
    'fully-free': 0,
    'stibet-free': 0,
    'stibet-partial': 0,
    'external': 0,
    'self-funded': 0,
  };

  const stibetByFundingModel: Record<FundingModel, number> = {
    'fully-free': 0,
    'stibet-free': 0,
    'stibet-partial': 0,
    'external': 0,
    'self-funded': 0,
  };

  const financialsByCategory: Record<Category, { revenue: number; cost: number; stibet: number; netBalance: number }> = {
    Culture: { revenue: 0, cost: 0, stibet: 0, netBalance: 0 },
    Drinking: { revenue: 0, cost: 0, stibet: 0, netBalance: 0 },
    Sports: { revenue: 0, cost: 0, stibet: 0, netBalance: 0 },
    Social: { revenue: 0, cost: 0, stibet: 0, netBalance: 0 },
    Academic: { revenue: 0, cost: 0, stibet: 0, netBalance: 0 },
    Trip: { revenue: 0, cost: 0, stibet: 0, netBalance: 0 },
    Art: { revenue: 0, cost: 0, stibet: 0, netBalance: 0 },
    Other: { revenue: 0, cost: 0, stibet: 0, netBalance: 0 },
  };

  const eventDetails: Record<string, EventFinancialDetails> = {};

  for (const ev of events) {
    const detail = calculateEventFinancials(ev);
    eventDetails[ev.id] = detail;

    totalFixedCosts += detail.fixedCost;
    totalVariableCostsAtCap += detail.variableCost;
    totalCostAtCap += detail.totalCost;
    totalNonFundableCosts += detail.nonFundableCost;

    totalStibetFundingUsed += detail.appliedStibetFunding;
    totalStibetBufferSavings += (detail.maxStibetAllowanceRaw - detail.maxStibetWithBuffer);

    totalExternalFunding += detail.externalFunding;
    totalParticipantRevenue += detail.projectedParticipantRevenue;
    totalNetRevenueAfterTax += detail.netRevenueAfterAppFeeAndTax;
    totalSectionNetBalance += detail.sectionNetBalance;

    totalCapacity += detail.capacity;
    totalOrganisersAssigned += detail.organisers;

    const cat = ev.category || 'Other';
    categoryBreakdown[cat] = (categoryBreakdown[cat] || 0) + 1;
    categorySpots[cat] = (categorySpots[cat] || 0) + detail.capacity;
    categoryOrgs[cat] = (categoryOrgs[cat] || 0) + detail.organisers;

    eventsByFundingModel[detail.fundingModel] = (eventsByFundingModel[detail.fundingModel] || 0) + 1;
    stibetByFundingModel[detail.fundingModel] = (stibetByFundingModel[detail.fundingModel] || 0) + detail.appliedStibetFunding;

    if (!financialsByCategory[cat]) {
      financialsByCategory[cat] = { revenue: 0, cost: 0, stibet: 0, netBalance: 0 };
    }
    financialsByCategory[cat].revenue += detail.projectedParticipantRevenue;
    financialsByCategory[cat].cost += detail.totalCost;
    financialsByCategory[cat].stibet += detail.appliedStibetFunding;
    financialsByCategory[cat].netBalance += detail.sectionNetBalance;
  }

  const overallRatio = totalOrganisersAssigned > 0 ? totalCapacity / totalOrganisersAssigned : 0;

  const categoryRatios = {} as Record<Category, CategoryRatioInfo>;

  for (const cat of categories) {
    const spots = categorySpots[cat] || 0;
    const orgs = categoryOrgs[cat] || 0;
    const ratio = orgs > 0 ? spots / orgs : (spots > 0 ? Infinity : 0);
    const maxThreshold = config?.categoryMaxRatioThresholds?.[cat]
      ?? DEFAULT_CATEGORY_MAX_RATIOS[cat]
      ?? config?.maxParticipantsPerOrganiserDefault
      ?? 20;
    const isExceeded = ratio > maxThreshold;

    categoryRatios[cat] = {
      spots,
      organisers: orgs,
      ratio,
      maxThreshold,
      isExceeded,
    };
  }

  return {
    totalEvents: events.length,
    totalSpots: totalCapacity,
    totalCapacity,
    totalOrganisersAssigned,
    overallRatio,

    totalFixedCosts,
    totalVariableCostsAtCap,
    totalCostAtCap,
    totalNonFundableCosts,

    totalStibetFundingUsed,
    totalStibetBufferSavings,

    totalExternalFunding,
    totalParticipantRevenue,
    totalNetRevenueAfterTax,
    totalSectionNetBalance,

    eventsByFundingModel,
    stibetByFundingModel,
    financialsByCategory,
    categoryRatios,
    categoryBreakdown,
    eventDetails,
  };
}


